import React, { useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Button,
  DialogContentText,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import fr from "date-fns/locale/fr";

const RefundForm = ({
  refundAmount,
  onSubmit,
  onCancel,
  colors,
  saleId,
  paymentId,
}) => {
  const [formData, setFormData] = useState({
    id_vente: saleId,
    id_paiement_tranche: paymentId,
    montant_refund: refundAmount,
    date_refund: new Date(),
    mode_refund: "",
    reference_refund: "",
    motif_refund: "",
    commentaire: "",
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.mode_refund)
      newErrors.mode_refund = "Le mode de remboursement est requis";
    if (!formData.motif_refund)
      newErrors.motif_refund = "Le motif du remboursement est requis";
    if (!formData.date_refund) newErrors.date_refund = "La date est requise";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when field is filled
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, color: colors?.grey[100] }}>
        Formulaire de remboursement
      </Typography>

      <DialogContentText sx={{ color: colors?.grey[100], mb: 3 }}>
        Montant à rembourser :{" "}
        {refundAmount.toLocaleString("fr-FR", {
          style: "currency",
          currency: "MAD",
        })}
      </DialogContentText>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <DatePicker
            label="Date du remboursement"
            value={formData.date_refund}
            onChange={(newValue) => {
              setFormData((prev) => ({
                ...prev,
                date_refund: newValue,
              }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={!!errors.date_refund}
                helperText={errors.date_refund}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: colors?.grey[100],
                    "& fieldset": { borderColor: colors?.grey[100] },
                  },
                  "& .MuiInputLabel-root": { color: colors?.grey[100] },
                }}
              />
            )}
          />
        </LocalizationProvider>

        <TextField
          select
          fullWidth
          name="mode_refund"
          label="Mode de remboursement"
          value={formData.mode_refund}
          onChange={handleChange}
          error={!!errors.mode_refund}
          helperText={errors.mode_refund}
          required
          sx={{
            "& .MuiOutlinedInput-root": {
              color: colors?.grey[100],
              "& fieldset": { borderColor: colors?.grey[100] },
            },
            "& .MuiInputLabel-root": { color: colors?.grey[100] },
          }}
        >
          {["Espèces", "Chèque", "Virement", "Carte"].map((mode) => (
            <MenuItem
              key={mode}
              value={mode}
              sx={{
                backgroundColor: colors?.primary[100],
                "&:hover": { backgroundColor: colors?.primary[900] },
              }}
            >
              {mode}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          name="reference_refund"
          label="Référence"
          value={formData.reference_refund}
          onChange={handleChange}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: colors?.grey[100],
              "& fieldset": { borderColor: colors?.grey[100] },
            },
            "& .MuiInputLabel-root": { color: colors?.grey[100] },
          }}
        />

        <TextField
          fullWidth
          name="motif_refund"
          label="Motif du remboursement"
          value={formData.motif_refund}
          onChange={handleChange}
          error={!!errors.motif_refund}
          helperText={errors.motif_refund}
          required
          multiline
          rows={2}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: colors?.grey[100],
              "& fieldset": { borderColor: colors?.grey[100] },
            },
            "& .MuiInputLabel-root": { color: colors?.grey[100] },
          }}
        />

        <TextField
          fullWidth
          name="commentaire"
          label="Commentaires additionnels"
          value={formData.commentaire}
          onChange={handleChange}
          multiline
          rows={2}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: colors?.grey[100],
              "& fieldset": { borderColor: colors?.grey[100] },
            },
            "& .MuiInputLabel-root": { color: colors?.grey[100] },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button onClick={onCancel} sx={{ color: colors?.grey[100] }}>
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="secondary"
          disabled={!formData.mode_refund || !formData.motif_refund}
          sx={{
            backgroundColor: colors?.blueAccent[700],
            "&:hover": { backgroundColor: colors?.blueAccent[800] },
          }}
        >
          Confirmer le remboursement
        </Button>
      </Box>
    </Box>
  );
};

export default RefundForm;
